/**
 * <rider-flyby> — rides a decorative image across the viewport once on page load.
 *
 * All visuals are CSS; this element only decides WHEN to start (immediate vs.
 * after full load + delay), whether to play at all this session (once-per-visit),
 * and cleans up after the ride. Adds the `running` attribute to kick off the CSS
 * animation. Respects prefers-reduced-motion.
 *
 * Data attributes (set in sections/rider-flyby.liquid):
 *   data-trigger  "immediate" | "after-load"
 *   data-delay    milliseconds to wait before starting
 *   data-once     "true" | "false"  (forced "false" in the theme editor so it replays)
 */
class RiderFlyby extends HTMLElement {
  static STORAGE_KEY = 'rider-flyby-seen';

  connectedCallback() {
    // Never animate for visitors who prefer reduced motion.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const once = this.dataset.once === 'true';
    if (once && this.#alreadySeen()) return;

    const delay = Math.max(0, parseInt(this.dataset.delay || '0', 10) || 0);
    const trigger = this.dataset.trigger || 'after-load';

    const start = () => {
      this.timer = window.setTimeout(() => {
        this.setAttribute('running', '');
        if (once) this.#markSeen();
      }, delay);
    };

    if (trigger === 'after-load' && document.readyState !== 'complete') {
      window.addEventListener('load', start, { once: true });
    } else {
      start();
    }

    // Once it has ridden off-screen, drop it so nothing lingers in a layer.
    this.addEventListener('animationend', this.#handleAnimationEnd);
  }

  disconnectedCallback() {
    if (this.timer) window.clearTimeout(this.timer);
  }

  #handleAnimationEnd = (event) => {
    // Two animations run (horizontal travel + vertical zig-zag); clean up only
    // when the travel animation finishes.
    if (typeof event.animationName === 'string' && event.animationName.indexOf('rider-flyby-x') === 0) {
      this.style.display = 'none';
    }
  };

  #alreadySeen() {
    try {
      return sessionStorage.getItem(RiderFlyby.STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  #markSeen() {
    try {
      sessionStorage.setItem(RiderFlyby.STORAGE_KEY, '1');
    } catch {
      /* private mode / storage disabled — just replay next load */
    }
  }
}

if (!customElements.get('rider-flyby')) {
  customElements.define('rider-flyby', RiderFlyby);
}
